import React, { useEffect, useState } from 'react';
import { Box, Flex, Text } from 'core/components';
import InterfaceLink from 'app/components/links/InterfaceLink';
import { formatBytesGreek } from 'core/util';
import snmpMetadata, { snmpType } from '@kentik/ui-shared/dictionary/snmpMetadata';
import { observer } from 'mobx-react';
import $devices from 'app/stores/device/$devices';

function getKeyLabel(key) {
  if (key === 'isAbove') {
    return 'Lower Sublayer Interface';
  }
  if (key === 'isBelow') {
    return 'Parent Interface';
  }
  return key.replace('if', '');
}

function getValue(deviceName, interfaceKvs, key) {
  const mapping = snmpMetadata[key];
  const value = interfaceKvs[key];
  if (mapping) {
    return `${mapping[value]} (${value})`;
  }
  if (key === 'isAbove' || key === 'isBelow') {
    if (!deviceName) {
      return value.split(',').join(', ');
    }

    return (
      <Text as={Flex} flexWrap="wrap" small>
        {value.split(',').map((snmpId) => (
          <InterfaceLink key={snmpId} snmp_id={snmpId} device_name={deviceName} mr="4px">
            {snmpId}
          </InterfaceLink>
        ))}
      </Text>
    );
  }
  return value;
}

function formatCapacity(capacity) {
  return capacity !== '0' ? formatBytesGreek(capacity * 1000000, '', 0.9991) : 'N/A';
}

const InterfaceKvsMetadata = ({ model }) => {
  const interfaceKvs = model.get('interfaceKvs') || {};
  const deviceId = model.get('device_id');
  const needsDeviceName = interfaceKvs.isAbove !== undefined || interfaceKvs.isBelow !== undefined;
  const [deviceName, setDeviceName] = useState(undefined);

  useEffect(() => {
    if (!needsDeviceName || !deviceId) {
      return undefined;
    }

    let isMounted = true;

    // isAbove/isBelow only carry sibling snmp ids, not the device name needed to build interface links,
    // so fetch it separately (lightweight bulk lookup, even for a single id).
    $devices.fetchDeviceStatusesById([deviceId]).then((deviceStatusesById) => {
      if (isMounted) {
        setDeviceName(deviceStatusesById[deviceId]?.device_name);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [deviceId, needsDeviceName]);

  const capacity = model.get('snmp_speed');
  const formattedCapacity = formatCapacity(capacity);
  const type = model.get('snmp_type');
  return (
    <>
      {type && (
        <Flex>
          <Box flex={1}>
            <Text small color="muted" fontWeight="normal" mr="4px">
              Type
            </Text>
          </Box>
          <Box flex={1}>
            <Text small>{snmpType[model.get('snmp_type')]}</Text>
            <Text small ml="4px" muted>
              ({model.get('snmp_type')})
            </Text>
          </Box>
        </Flex>
      )}
      <Flex>
        <Box flex={1}>
          <Text small color="muted" fontWeight="normal" mr="4px">
            Capacity
          </Text>
        </Box>
        <Text flex={1} small>
          {`${formattedCapacity} `}
          <br />
          {model.overriddenFields.includes('snmp_speed') && (
            <Text muted small>
              (Overridden from: {formatCapacity(model.get('initial_snmp_speed'))})
            </Text>
          )}
        </Text>
      </Flex>
      {Object.keys(interfaceKvs)
        .filter((key) => key !== 'updated')
        .map((key) => (
          <Flex key={key}>
            <Box flex={1}>
              <Text small color="muted" fontWeight="normal" mr="4px">
                {getKeyLabel(key)}
              </Text>
            </Box>
            <Text flex={1} small>
              {getValue(deviceName, interfaceKvs, key)}
            </Text>
          </Flex>
        ))}
    </>
  );
};

export default observer(InterfaceKvsMetadata);
